// utilities/payoutTimeline.js
// ---------------------------------------------------------------------------
// Pure helpers for the payout live-pipeline UI in views/WalletDashboard.js.
//
// Nothing in here touches React, the DOM or Date.now(): every function that
// needs "now" takes it as an argument, so the timers are deterministic under
// test and the component can feed a single shared ticker value to every row.
//
// Time convention: callers pass a SERVER-ALIGNED now (Date.now() + clockSkew)
// because every row timestamp and every scheduler `nextRunAt` is server time.
// See computeClockSkew().
// ---------------------------------------------------------------------------

export const QUEUE_DEFAULT_INTERVAL_MS   = 300000; // wallet queue runs every 5 min when no scheduler info
export const POLLER_DEFAULT_ELIGIBILITY_MS = 90000; // pollPendingStatuses eligibility when not reported

export const IN_FLIGHT_STATUSES = ["QUEUED", "VALIDATING", "PROCESSING", "PENDING"];

export const isInFlight = (row) => !!row && IN_FLIGHT_STATUSES.includes(row.status);

export const anyInFlight = (rows) => Array.isArray(rows) && rows.some(isInFlight);

/** Coerce an ISO string / Date / epoch number into epoch ms (or null). */
export const toMs = (v) => {
  if (v == null || v === "") return null;
  const t = v instanceof Date ? v.getTime() : typeof v === "number" ? v : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * skewMs = serverTime − receivedAt. Server "now" ≈ Date.now() + skewMs.
 * Returns 0 when either side is unusable so a missing serverTime never breaks
 * a countdown, it just falls back to the browser clock.
 */
export function computeClockSkew(serverTimeIso, receivedAtMs) {
  const s = toMs(serverTimeIso);
  const r = toMs(receivedAtMs);
  if (s == null || r == null) return 0;
  return s - r;
}

/** m:ss (or h:mm:ss past an hour). Never negative — 0:00 is the floor. */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${m}:${pad2(s)}`;
}

/** Human elapsed: "12s", "1m 12s", "2h 05m", "3d 4h". Never negative. */
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m ${pad2(total % 60)}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${pad2(m % 60)}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** Compact delta for timeline steps: "+49s", "+5m39s", "+1h02m". */
export function formatDelta(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return "";
  const total = Math.max(0, Math.round(Number(ms) / 1000));
  if (total < 60) return `+${total}s`;
  const m = Math.floor(total / 60);
  if (m < 60) return `+${m}m${pad2(total % 60)}s`;
  const h = Math.floor(m / 60);
  return `+${h}h${pad2(m % 60)}m`;
}

/** HH:MM:SS in the viewer's local time. "—" for anything unparsable. */
export function formatClock(date) {
  const t = toMs(date);
  if (t == null) return "—";
  const d = new Date(t);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

const RESOLVED_VIA_LABEL = {
  CALLBACK:       "Callback",
  POLL:           "Poll",
  TARGETED_CHECK: "Targeted check",
  ADMIN_POLL:     "Admin poll",
  RESEND_GUARD:   "Resend guard",
};

const RESOLVED_VIA_SHORT = {
  CALLBACK:       "callback",
  POLL:           "poll",
  TARGETED_CHECK: "targeted",
  ADMIN_POLL:     "admin poll",
  RESEND_GUARD:   "resend guard",
};

/**
 * CONFIRMATION column label. resolvedVia wins; legacy rows (no resolvedVia)
 * fall back to what the status alone can honestly say.
 */
export function confirmationLabel(row) {
  if (!row) return "—";
  const via = row.resolvedVia && RESOLVED_VIA_LABEL[row.resolvedVia];
  if (via) return via;
  switch (row.status) {
    case "SUCCESSFUL": return "Confirmed";
    case "FAILED":     return "Failed";
    case "PROCESSING": return "Awaiting MTN";
    default:           return "—";
  }
}

/** Label + chip tone for the CONFIRMATION cell. */
export function confirmationMeta(row) {
  const label = confirmationLabel(row);
  let tone = "grey";
  if (row?.status === "SUCCESSFUL") tone = "green";
  else if (row?.status === "FAILED") tone = "red";
  else if (row?.status === "PROCESSING") tone = "yellow";
  return { label, tone };
}

/**
 * Ordered lifecycle steps for one row:
 *   [{ key:"queued", at }, { key:"submitted", at, deltaMs },
 *    { key:"confirmed"|"failed", at, deltaMs, via }]
 * `at` values are epoch ms. Steps whose timestamp is unknown are omitted, so
 * a QUEUED row yields one step and a SUCCESSFUL one up to three.
 */
export function buildPayoutTimeline(row) {
  if (!row) return [];
  const steps = [];
  const queuedAt    = toMs(row.queuedAt) ?? toMs(row.createdAt);
  const submittedAt = toMs(row.submittedAt);
  if (queuedAt != null) steps.push({ key: "queued", at: queuedAt });
  if (submittedAt != null) {
    steps.push({
      key: "submitted",
      at: submittedAt,
      deltaMs: queuedAt != null ? submittedAt - queuedAt : null,
    });
  }
  if (row.status === "SUCCESSFUL" || row.status === "FAILED") {
    const doneAt = toMs(row.completedAt) ?? toMs(row.updatedAt);
    if (doneAt != null) {
      const prev = submittedAt ?? queuedAt;
      steps.push({
        key: row.status === "SUCCESSFUL" ? "confirmed" : "failed",
        at: doneAt,
        deltaMs: prev != null ? doneAt - prev : null,
        via: row.resolvedVia ? (RESOLVED_VIA_SHORT[row.resolvedVia] || String(row.resolvedVia).toLowerCase()) : null,
      });
    }
  }
  return steps;
}

/**
 * One compact line, e.g.
 *   "Queued 15:59:12 → MTN 16:04:51 (+5m39s) → ✓ 16:06:40 (+1m49s · poll)"
 */
export function formatTimeline(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return "";
  return steps
    .map((s) => {
      const head =
        s.key === "queued"    ? "Queued" :
        s.key === "submitted" ? "MTN" :
        s.key === "confirmed" ? "✓" : "✗";
      const extras = [];
      if (s.deltaMs != null) extras.push(formatDelta(s.deltaMs));
      if (s.via) extras.push(s.via);
      const tail = extras.length ? ` (${extras.join(" · ")})` : "";
      return `${head} ${formatClock(s.at)}${tail}`;
    })
    .join(" → ");
}

/** "retry 2 · next 16:09:00" for QUEUED rows that have been retried; else null. */
export function retryLabel(row) {
  if (!row || !(Number(row.retryCount) > 0)) return null;
  if (row.status !== "QUEUED" && row.status !== "VALIDATING" && row.status !== "PROCESSING") return null;
  const next = toMs(row.nextRetryAt);
  const base = `retry ${Number(row.retryCount)}`;
  return next != null && row.status === "QUEUED" ? `${base} · next ${formatClock(next)}` : base;
}

/**
 * What the row is waiting for right now.
 *
 * @param row        DisbursementRequest-shaped row
 * @param scheduler  the /admin/scheduler payload (`{ serverTime, jobs, queue }`) or null
 * @param nowMs      server-aligned now
 * @returns null for terminal rows, else
 *   { stage, label, targetMs|null, remainingMs|null, elapsedMs, atZeroLabel, hint }
 */
export function stageTarget(row, scheduler, nowMs) {
  if (!row) return null;
  const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : 0;
  const jobs = scheduler?.jobs || null;
  const startedAt = toMs(row.queuedAt) ?? toMs(row.createdAt);
  const done = (o) => ({
    ...o,
    remainingMs: o.targetMs != null ? o.targetMs - now : null,
    elapsedMs: Math.max(0, o.elapsedMs || 0),
  });

  switch (row.status) {
    case "QUEUED":
    case "PENDING": {
      const elapsedMs = startedAt != null ? now - startedAt : 0;
      const nextRetryAt = toMs(row.nextRetryAt);
      if (nextRetryAt != null && nextRetryAt > now) {
        return done({
          stage: "QUEUED", label: `Retry #${Number(row.retryCount) || 1} in`,
          targetMs: nextRetryAt, elapsedMs, atZeroLabel: "Retrying…", hint: null,
        });
      }
      const qp = jobs?.queueProcessor;
      const qpNext = toMs(qp?.nextRunAt);
      if (qpNext != null) {
        const busy = !!(qp.kickPending || qp.running);
        return done({
          stage: "QUEUED", label: "Submits to MTN in", targetMs: qpNext, elapsedMs,
          atZeroLabel: busy ? "Submitting…" : "Queue run due…",
          hint: qp.kickPending ? "kick pending" : null,
        });
      }
      const every = Math.max(1, Math.round((Number(qp?.intervalMs) || QUEUE_DEFAULT_INTERVAL_MS) / 60000));
      return done({
        stage: "QUEUED", label: "Queued", targetMs: null, elapsedMs,
        atZeroLabel: null, hint: `queue runs every ${every} min`,
      });
    }

    case "VALIDATING": {
      const from = toMs(row.validatedAt) ?? startedAt;
      return done({
        stage: "VALIDATING", label: "Submitting to MTN…", targetMs: null,
        elapsedMs: from != null ? now - from : 0, atZeroLabel: null, hint: null,
      });
    }

    case "PROCESSING": {
      const submittedAt = toMs(row.submittedAt) ?? startedAt;
      const elapsedMs = submittedAt != null ? now - submittedAt : 0;
      let target = toMs(row.nextStatusCheckAt);
      if (target == null) {
        const sp = jobs?.statusPoller;
        if (sp) {
          const eligibilityMs = Number(sp.eligibilityMs) > 0 ? Number(sp.eligibilityMs) : POLLER_DEFAULT_ELIGIBILITY_MS;
          const eligibleAt = submittedAt != null ? submittedAt + eligibilityMs : null;
          let pollerNext = toMs(sp.nextRunAt);
          const intervalMs = Number(sp.intervalMs) > 0 ? Number(sp.intervalMs) : null;
          // The poller only picks the row up on the first run AFTER it becomes
          // eligible, so step the run time forward rather than promising the
          // eligibility instant itself.
          if (pollerNext != null && eligibleAt != null && intervalMs && pollerNext < eligibleAt) {
            const steps = Math.ceil((eligibleAt - pollerNext) / intervalMs);
            pollerNext += steps * intervalMs;
          }
          const candidates = [pollerNext, eligibleAt].filter((v) => v != null);
          if (candidates.length) target = Math.max(...candidates);
        }
      }
      if (target != null) {
        return done({
          stage: "PROCESSING", label: "MTN status check in", targetMs: target,
          elapsedMs, atZeroLabel: "Checking MTN…", hint: null,
        });
      }
      return done({
        stage: "PROCESSING", label: "Awaiting MTN confirmation", targetMs: null,
        elapsedMs, atZeroLabel: null, hint: null,
      });
    }

    default:
      return null;
  }
}

/**
 * Render a stageTarget() result as one line. Never wraps: once the target is
 * reached it switches to the atZero label + time in stage.
 *   "Submits to MTN in 4:12"
 *   "Submitting… · in stage 1m 12s"
 *   "Queued 3m 12s ago · queue runs every 5 min"
 *   "Submitting to MTN… · 12s"
 */
export function formatStage(st) {
  if (!st) return "";
  if (st.targetMs != null) {
    if (st.remainingMs > 0) return `${st.label} ${formatCountdown(st.remainingMs)}`;
    return `${st.atZeroLabel || "Due"} · in stage ${formatElapsed(st.elapsedMs)}`;
  }
  if (st.stage === "QUEUED") {
    return `${st.label} ${formatElapsed(st.elapsedMs)} ago${st.hint ? ` · ${st.hint}` : ""}`;
  }
  return `${st.label} · ${formatElapsed(st.elapsedMs)}`;
}

/**
 * Merge a disbursement:statusUpdate socket payload into a row. The wallet
 * sends every contract key, null when unknown — a null must not erase a value
 * the list endpoint already gave us (payeeName, amount …). Only the failure
 * fields may be cleared, and only by a non-failed status.
 */
export function applyStatusUpdate(row, update) {
  if (!row || !update) return row;
  const next = { ...row };
  Object.keys(update).forEach((k) => {
    const v = update[k];
    if (v !== null && v !== undefined) next[k] = v;
  });
  // A row moving to any non-failed status (retry re-queued, submitted, or
  // confirmed) no longer has a current failure — drop the stale reason.
  if (update.status && update.status !== "FAILED" && update.failureReason == null) {
    next.failureReason = null;
    next.failureCode = null;
  }
  if (typeof update.callbackReceived === "boolean") next.callbackReceived = update.callbackReceived;
  return next;
}

/** Patch by disbursementId inside a list; returns the same array when nothing matched. */
export function patchRows(rows, update) {
  if (!Array.isArray(rows) || !update?.disbursementId) return rows;
  let touched = false;
  const out = rows.map((r) => {
    if (r?.disbursementId !== update.disbursementId) return r;
    touched = true;
    return applyStatusUpdate(r, update);
  });
  return touched ? out : rows;
}

const LIFECYCLE_TITLES = {
  queued:    "Queued for payout",
  submitted: "Submitted to MTN",
  confirmed: "Confirmed successful",
  failed:    "Failed",
};

/**
 * Audit drawer feed: the row's own lifecycle (buildPayoutTimeline) merged
 * chronologically with wallet AuditLog rows
 * ({ action, performedBy, timestamp, beforeState, afterState }).
 * Audit rows for OTHER entities are dropped — an older wallet ignores the
 * entityId filter and would otherwise hand back unrelated history.
 */
export function mergeAuditTimeline(row, logs, allowedIds) {
  const ids = new Set((allowedIds || [row?.disbursementId, row?._id]).filter(Boolean).map(String));
  const out = buildPayoutTimeline(row).map((s) => ({
    kind: "lifecycle",
    key: `life-${s.key}`,
    at: s.at,
    title: LIFECYCLE_TITLES[s.key] || s.key,
    subtitle: [s.deltaMs != null ? formatDelta(s.deltaMs) : null, s.via ? `via ${s.via}` : null].filter(Boolean).join(" · ") || null,
  }));
  // An attempt MTN refused before a retry went through. It is history, not
  // the row's state: it reads as a step on the line, never as a banner.
  const prior = row?.previousAttemptFailure;
  if (prior?.reason) {
    out.push({
      kind: "lifecycle",
      key: "life-prior-attempt",
      at: toMs(prior.at) ?? null,
      title: `Attempt ${Number(prior.attempt) + 1 || 1} refused by MTN`,
      subtitle: `${prior.reason}${prior.code ? ` (${prior.code})` : ""} · retried`,
    });
  }
  (Array.isArray(logs) ? logs : []).forEach((l, i) => {
    if (!l) return;
    if (l.entityId != null && ids.size && !ids.has(String(l.entityId))) return;
    out.push({
      kind: "audit",
      key: `audit-${l._id || i}`,
      at: toMs(l.timestamp) ?? toMs(l.createdAt) ?? null, // unknown time sorts last
      title: String(l.action || "AUDIT").replace(/_/g, " "),
      subtitle: l.performedBy ? `by ${l.performedBy}` : null,
      detail: l.afterState || l.beforeState || null,
    });
  });
  return out.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity));
}

/**
 * Hero queue strip: "Queue · 3 waiting · submits in 1:42 · last run 16:04:51
 * (12 submitted) · MTN check every 120s". Parts that the scheduler did not
 * report are simply left out.
 */
export function formatQueueStrip(scheduler, nowMs) {
  if (!scheduler) return "";
  const parts = ["Queue"];
  const q = scheduler.queue || {};
  const qp = scheduler.jobs?.queueProcessor || {};
  const sp = scheduler.jobs?.statusPoller || {};
  if (q.queuedNow != null) parts.push(`${Number(q.queuedNow)} waiting`);
  const next = toMs(qp.nextRunAt);
  if (qp.running) parts.push("submitting now");
  else if (next != null) {
    const rem = next - Number(nowMs || 0);
    parts.push(rem > 0 ? `submits in ${formatCountdown(rem)}` : (qp.kickPending ? "submitting…" : "run due"));
  }
  const last = toMs(qp.lastRunAt);
  if (last != null) {
    const r = qp.lastResult || {};
    const n = [r.succeeded, r.submitted, r.processed].find((v) => typeof v === "number");
    parts.push(`last run ${formatClock(last)}${n != null ? ` (${n} submitted)` : ""}`);
  }
  if (Number(sp.intervalMs) > 0) parts.push(`MTN check every ${Math.round(Number(sp.intervalMs) / 1000)}s`);
  return parts.join(" · ");
}
